import { ControlType } from "../controltype";
import { ColumnChooserVm } from "../datagrid/column-chooser-vm";
import { MessageHelper } from "../../helper/message-helper";
import { Logger } from "../../logger/logger";
import { ObjectHelper } from "../../helper/object-helper";
import { dx } from "../ui-options-types-wrapper";
import { DateHelper } from "../../helper/date-helper";
import { KoExtend } from "../../extend/knockout-extend";
import { Debugging } from "../../core/debugging/debugging";
import { StateStoringCopyDialogVm } from "../../dialog/statestoring-copy-dialog";
import { SqafSettingStorage } from "../../core/settings/sqaf-setting-storage";
import { NautilusFeatureName } from "../../types/NautilusFeatureName";
import { SharedLang } from "../../../Generated/Shared-lang";
import { ExportToExcel } from "../export/export-data-grid";

export enum ColumnChooserType {
	None = 0,
	Standard = 1,
	Custom = 2
}

export class DefaultSettings {
	public static readonly defaultPrecision = 2;

	public static readonly defaultCurrencyFormat: DevExpress.ui.Format = {
		type: "fixedPoint",
		precision: DefaultSettings.defaultPrecision
	};

	public static readonly defaultNumberFormat: DevExpress.ui.Format = {
		type: "fixedPoint",
		precision: DefaultSettings.defaultPrecision
	};

	public static readonly defaultNumberPrecision2Format: DevExpress.ui.Format = {
		type: "fixedPoint",
		precision: 2
	};

	public static readonly defaultNumberPrecision3Format: DevExpress.ui.Format = {
		type: "fixedPoint",
		precision: 3
	};

	public static readonly defaultNumberPrecision5Format: DevExpress.ui.Format = {
		type: "fixedPoint",
		precision: 5
	};

	public static readonly plainIntegerFormat: DevExpress.ui.Format = {
		type: "decimal"
	};

	public static readonly zeroPrecisionNumberFormat: DevExpress.ui.Format = {
		type: "fixedPoint",
		precision: 0
	};

	/**
	 * Please don't use this anymore. Use DefaultSettings.defaultNumberPrecision2Format instead.
	 */
	public static readonly defaultPercentageFormat: DevExpress.ui.Format = {
		type: "percent",
		precision: DefaultSettings.defaultPrecision,
		currency: null as string
	};

	public static readonly defaultPercentagePrecision5Format: DevExpress.ui.Format = {
		type: "percent",
		precision: 5,
		currency: null as string
	};

	public static readonly decimalWithSixPrecisionFormat = "#,##0.000000";
	public static readonly decimalWithFivePrecisionFormat = "#,##0.00000";
	public static readonly decimalWithFourPrecisionFormat = "#,##0.0000";
	public static readonly decimalWithTwoPrecisionFormat = "#,##0.00";
	public static readonly decimalWithZeroPrecisionFormat = "#";
	public static readonly classGridColumnSelectBox = "checkbox-cell-height-fix";

	public static readonly defaultGermanDateFormat = "dd.MM.yyyy";
	public static readonly defaultGermanDateTimeFormat = "dd.MM.yyyy HH:mm";
	public static readonly defaultGermanDateTimeWithSecondsFormat = "dd.MM.yyyy HH:mm:ss";
	public static readonly defaultGermanTimeFormat = "HH:mm";

	public static readonly numberAlignment = "right";
	public static readonly actionAlignment = "center";
	public static readonly booleanAlignment = "center";
	public static readonly widthAction = 60;
	public static readonly widthIconsOnly = 45;
	public static readonly widthDate = 80;
	public static readonly widthDateTime = 110;
	public static readonly widthPeriod = 115;
	public static readonly widthNumber = 80;
	public static readonly widthPercentage = 40;
	public static readonly widthBoolean = 53;
	public static readonly widthArchived = 90;
	public static readonly widthYear = 45;
	public static readonly widthPosSubPos = 60;

	public static readonly button: DevExpress.ui.dxButtonOptions = freeze({});

	public static readonly checkBox: DevExpress.ui.dxCheckBoxOptions = freeze({});

	public static readonly threeStateCheckbox: DevExpress.ui.dxCheckBoxOptions = freeze({
		onInitialized: (e) => {
			let updating = false;
			let checkbox = e.component as DevExpress.ui.dxCheckBox;
			if (checkbox.option("value") === null) {
				checkbox.option("value", undefined);
			}
			let originalChangedHandler = checkbox.option("onValueChanged");
			checkbox.option("onValueChanged", (valueChangeEvent: any) => {
				if (!updating && !valueChangeEvent.event) {
					if (valueChangeEvent.value === null) {
						updating = true;
						checkbox.option("value", undefined);
						updating = false;
					}
					return;
				}

				let newValue: boolean;
				if (valueChangeEvent.previousValue === true) {
					newValue = false;
				} else if (valueChangeEvent.previousValue === false) {
					newValue = undefined;
				} else {
					newValue = true;
				}

				if (!updating) {
					updating = true;
					checkbox.option("value", newValue);
					updating = false;
				}
				if (originalChangedHandler != null && typeof originalChangedHandler === "function") {
					originalChangedHandler(valueChangeEvent);
				}
			});
		}
	});

	public static readonly radioGroup: DevExpress.ui.dxRadioGroupOptions = freeze({});

	public static readonly dataGrid: DevExpress.ui.dxDataGridOptions = freeze({
		searchPanel: {
			visible: true,
			searchVisibleColumnsOnly: true,
			placeholder: "Filter…"
		},
		columnChooser: {
			enabled: true,
			mode: "select",
			allowSearch: true
		},
		headerFilter: {
			allowSearch: true,
			visible: true
		},
		paging: {
			enabled: true,
			pageSize: 20,
		},
		pager: {
			showNavigationButtons: true,
			showPageSizeSelector: true,
			allowedPageSizes: [10, 20, 50, 100],
		},
		scrolling: {
			mode: "standard",
			showScrollbar: "always"
		},
		selection: {
			mode: "single"
		},
		export: {
			enabled: true,
			fileName: `Export`
		},
		columnResizingMode: "widget",
		onExporting: (e) => {
			ExportToExcel.exportDataGrid(e);
		},
		allowColumnReordering: true,
		allowColumnResizing: true,
		rowAlternationEnabled: true,
		hoverStateEnabled: true,
		onToolbarPreparing: (e: any) => {
			let columnChooserEnabled = e.element.data("columnchooser-enabled");
			let useColumnChooser = columnChooserEnabled !== false;
			DefaultSettings.appendDefaultToolbarItems(e, useColumnChooser);
		}
	});

	public static readonly treeList: DevExpress.ui.dxTreeListOptions = freeze({
		hoverStateEnabled: true
	});

	public static readonly treeView: DevExpress.ui.dxTreeViewOptions = freeze({});

	public static pivotGrid: DevExpress.ui.dxPivotGridOptions = freeze({});

	public static chart: DevExpress.viz.charts.dxChartOptions = freeze({});

	public static readonly dateBox: DevExpress.ui.dxDateBoxOptions = freeze({
		displayFormat: "dd.MM.yyyy",
		min: new Date(2010, 0, 1),
		max: new Date(2100, 0, 1),
		useMaskBehavior: true,
		width: "100%",
		calendarOptions: DefaultSettings.getCalendarOptionsWithCalendarWeek()
	});

	public static readonly dateBoxWithoutCalendarWeek: DevExpress.ui.dxDateBoxOptions = freeze({
		displayFormat: "dd.MM.yyyy",
		min: new Date(2010, 0, 1),
		max: new Date(2100, 0, 1),
		useMaskBehavior: true,
		width: "100%",
	});

	public static readonly dateBoxWithTime: DevExpress.ui.dxDateBoxOptions = freeze({
		displayFormat: "dd.MM.yyyy HH:mm",
		min: new Date(2010, 0, 1),
		max: new Date(2100, 0, 1),
		width: "100%",
		// TODO (oa) add CalendarWeek column?
	});

	public static readonly selectTreeBox: DevExpress.ui.dxSelectBoxOptions<any> = freeze({
		showClearButton: true,
		valueExpr: "id",
		displayExpr: "name",
	});

	public static readonly selectTreeView: DevExpress.ui.dxTreeViewOptions = freeze({
		dataStructure: "plain",
		keyExpr: "id",
		selectByClick: true,
		selectNodesRecursive: false,
		showCheckBoxesMode: "normal",
		displayExpr: "name",
		parentIdExpr: "parentId",
		selectionMode: "single",
	});

	public static readonly form: DevExpress.ui.dxFormOptions = freeze({});

	public static readonly lookup: DevExpress.ui.dxLookupOptions = freeze({});

	public static readonly textArea: DevExpress.ui.dxTextBoxOptions<any> = freeze({
		valueChangeEvent: "keyup input"
	});

	public static readonly textBox: DevExpress.ui.dxTextBoxOptions<any> = freeze({
		valueChangeEvent: "keyup input"
	});

	public static readonly autocomplete: DevExpress.ui.dxAutocompleteOptions = freeze({
		showClearButton: true,
		showDropDownButton: true,
		openOnFieldClick: true,
		minSearchLength: 0,
		valueChangeEvent: "input change keyup"
	});

	public static readonly numberBox: DevExpress.ui.dxNumberBoxOptions = freeze({
		valueChangeEvent: "keyup input", // Bug 28403
	});

	public static readonly decimalBox: DevExpress.ui.dxNumberBoxOptions = freeze({
		valueChangeEvent: "keyup input", // Bug 28403
		format: "#,##0.00"
	});

	public static readonly searchBox: DevExpress.ui.dxTextBoxOptions<any> = freeze({
		placeholder: "Filter...",
		width: 300,
		mode: "search",
		valueChangeEvent: "keyup"
	});

	public static readonly fileUploader: DevExpress.ui.dxFileUploaderOptions = freeze({
	});

	public static readonly slider: DevExpress.ui.dxSliderOptions = freeze({});

	public static readonly htmlEditor: DevExpress.ui.dxHtmlEditorOptions = freeze({
		visible: ko.observable(true) as any,
		toolbar: {
			items: [
				"bold", "italic", "strike", "underline",
				"separator",
				{
					name: "size",
					acceptedValues: ["6pt", "7pt", "8pt", "10pt", "12pt", "14pt", "16pt"],
					options: {
						placeholder: "Größe"
					}
				},
				"separator",
				{
					name: "font",
					acceptedValues: ["Arial", "Courier New", "Georgia", "Impact", "Lucida Console", "Tahoma", "Times New Roman", "Verdana"]
				},
				"separator",

				{
					name: "header",
					acceptedValues: [false, 1, 2, 3, 4, 5]
				},
				"separator",
				"alignLeft", "alignCenter", "alignRight", "alignJustify",
				"separator",
				"bulletList", "orderedList", "decreaseIndent", "increaseIndent",
				"separator",
				"insertTable",
				"deleteTable",
				"insertRowAbove",
				"insertRowBelow",
				"deleteRow",
				"insertColumnLeft",
				"insertColumnRight",
				"deleteColumn",
			]
		},
		variables: {
			dataSource: [
				"Belegnummer",
				"Belegnummernliste",
				"Belegtyp",
				"Belegzeitraum",
				"Belegaufstellung",
				"Auftragsnummer",
				"Auftragsnummernliste",
				"Versender-Name",
				"Versender-Mail",
				"Versender-Telefon",
				"Versender-Fax",
				"Logo"
			],
			escapeChar: ["[", "]"]
		}
	});

	public static readonly dropDownBox: DevExpress.ui.dxDropDownBoxOptions = freeze({});

	public static readonly list: DevExpress.ui.dxListOptions = freeze({
		showSelectionControls: true,
		selectionMode: "multiple",
		showScrollbar: "always",
		itemTemplate: (item, _, element) => {
			element.append(item.name);
		},
		keyExpr: "id"
	});

	public static readonly selectBox: DevExpress.ui.dxSelectBoxOptions<any> = freeze({
		searchEnabled: true,
		showClearButton: true
	});

	public static readonly tagBox: DevExpress.ui.dxTagBoxOptions = freeze({
		showSelectionControls: true,
		displayExpr: "name",
		valueExpr: "id",
		selectAllMode: "allPages",
		maxDisplayedTags: 3, // for all customers! Don't remove careless
		searchEnabled: true,
		showDropDownButton: true,
		onValueChanged: DefaultSettings.sortTagBoxOnValueChanged,
		onOpened: DefaultSettings.sortTagBoxDataSourceOnOpened,
		showClearButton: true,
		multiline: !SqafSettingStorage.getInstance()
			.getSettingOrDefault(NautilusFeatureName.CommonTagBoxPreventMultiline, false).getBoolean(0, false),
	});

	/**
	 * DynamicTypeExtension can load settings from html attributes dynamically, at runtime
	 * @param type Type of the control attributes should be extended
	 */
	public static dynamicTypeExtension = (type: ControlType): DevExpress.ui.WidgetOptions<any> => {
		switch (type) {
			default:
				return {
				}
		}
	}

	public static mergeWithDefault = (type: ControlType, localSettings: object): DevExpress.ui.WidgetOptions<any> => {
		let defaultSettings = DefaultSettings[type] as DevExpress.ui.WidgetOptions<any>;
		if (defaultSettings == null) {
			Logger.error(`Could not find default settings for type '${type}'`);
			defaultSettings = {};
		}

		return $.extend(true, {}, defaultSettings, DefaultSettings.dynamicTypeExtension(type), localSettings);
	}

	public static appendToolbarItems = (e: any, options?: IToolbarAppendOptions): IToolbarAppendResult => {
		let defaultOptions = {
			layout: {
				columnChooser: ColumnChooserType.Standard,
				reset: true,
				save: true,
				autoWidth: false,
				resizingMode: true,
				copy: true,
				isEnabled: true
			},
			reload: true,
			excelExport: true,
			filter: true
		} as IToolbarAppendOptions;
		options = (ObjectHelper.getDeepMerged(options, defaultOptions)) as IToolbarAppendOptions;

		let elementId = e.element.attr("id");

		let toolbarItems: any[] = e.toolbarOptions.items;

		let excelExportIndex = toolbarItems.findIndex((value) => value && value.name === "exportButton");
		let excelExportItem: any = null;
		if (excelExportIndex >= 0) {
			let excelExportItems = toolbarItems.splice(excelExportIndex, 1);
			if (excelExportItems && excelExportItems.length > 0) {
				excelExportItem = excelExportItems[0];
			}
		}

		toolbarItems = toolbarItems.reverse();

		let optionMenuItems: any[] = [];

		if (options.layout && options.layout.isEnabled) {
			let columnChooserIndex = toolbarItems.findIndex((value) => value && value.name === "columnChooserButton");
			let columnChooserType = options.layout.columnChooser;
			if (columnChooserType === false || (!(typeof columnChooserType === "boolean") && columnChooserType !== ColumnChooserType.Standard)) {
				if (columnChooserIndex > 0) {
					toolbarItems.splice(columnChooserIndex, 1);
				}
				if (columnChooserType === false || columnChooserType === ColumnChooserType.Custom) {
					toolbarItems.push({
						widget: "dxButton",
						options: {
							hint: "Spaltenauswahl",
							icon: "column-chooser",
							elementAttr: {
								id: `${elementId}-columnchooser`
							},
							onClick: () => {
								try {
									ColumnChooserVm.getColumnChooser(elementId).show();
								} catch (err) {
									Logger.error(err);
									MessageHelper.uiError("Es konnte kein Spaltenauswahldialog geladen werden.");
								}
							}
						},
					});
				}
			} else if (columnChooserIndex > 0) {
				let columnChooserItems = toolbarItems.splice(columnChooserIndex, 1);
				if (columnChooserItems && columnChooserItems.length > 0) {
					toolbarItems.push(columnChooserItems[0]);
				}
			}

			// save layout
			if (options.layout.save) {
				optionMenuItems.push({
					icon: "fa fa-save",
					text: "Layout speichern",
					width: 50,
					onClick: () => {
						try {
							let currentState = e.component.state();

							// workaround since devextreme 19.2.3 can not save
							delete currentState.allowedPageSizes;

							currentState.timestamp = DateHelper.nowAsTicks();
							e.component.state(currentState);
						} catch (err) {
							MessageHelper.uiError("Das Layout konnte nicht gespeichert werden");
							Logger.error("Could not save layout");
						}
					},
				});
			}

			// reload datasource
			if (options.reload) {
				optionMenuItems.push({
					text: "Aktualisiere Daten",
					icon: "fa fa-refresh",
					width: 50,
					onClick: async () => {
						try {
							// deselect all to trigger reload if first row is selected with new values
							e.component.deselectAll();
							await e.component.getDataSource().reload();
							MessageHelper.uiSuccess("Die Daten wurden aktualisiert");
						} catch (err) {
							MessageHelper.uiError("Die Daten konnten nicht aktualisiert werden");
							Logger.error("Could not refresh data");
						}
					},
				});
			}

			// reset layout
			if (options.layout.reset) {
				optionMenuItems.push({
					text: "Layout zurücksetzen",
					icon: "fa fa-undo",
					width: 50,
					onClick: () => {
						try {
							e.component.state({});
							MessageHelper.uiSuccess("Das Layout wurde zurückgesetzt");
						} catch (err) {
							MessageHelper.uiError("Das Layout konnte nicht zurückgesetzt werden");
							Logger.error("Could not reset layout");
						}
					},
				});
			}

			// reset layout and save
			if (options.layout.save) {
				optionMenuItems.push({
					text: "Layout löschen",
					icon: "fa fa-trash",
					width: 50,
					onClick: () => {
						try {
							e.component.state({ timestamp: DateHelper.nowAsTicks() });
						} catch (err) {
							MessageHelper.uiError("Das Layout konnte nicht zurückgesetzt werden");
							Logger.error("Could not reset layout");
						}
					}
				});
			}

			// reset layout and save only makes sense if you can also save
			if (options.layout.copy && options.layout.save) {
				optionMenuItems.push({
					text: "Layout von anderem Benutzer kopieren",
					icon: "fa fa-copy",
					width: 50,
					onClick: () => {
						try {
							let stateStoringKey = e.component.option("stateStoring")?.storageKey;
							if (stateStoringKey == null) {
								return;
							}
							StateStoringCopyDialogVm.getInstance().open(KoExtend.resolve(stateStoringKey), (stateStr, save) => {
								let state = JSON.parse(stateStr);
								e.component.state(state);
								if (save) {
									let currentState = e.component.state();

									// workaround since devextreme 19.2.3 can not save
									delete currentState.allowedPageSizes;

									currentState.timestamp = DateHelper.nowAsTicks();
									e.component.state(currentState);
								}
							});
						} catch (err) {
							MessageHelper.uiError("");
							Logger.error("");
						}
					},
					visible: ko.computed(() => {
						let dialog = $("#statestoring-copy-dialog");
						let stateStoringKey = e.component?.option("stateStoring")?.storageKey;
						return dialog?.length > 0 && stateStoringKey != null;
					})
				});
			}

			if (options.layout.resizingMode && e?.model?.columnAutoWidthActivated) {
				e.model.columnResizeModeActivated(e.component.option("columnResizingMode") !== "nextColumn");
				let textActivatedDeactived = ko.computed(() => {
					return `Spalten mitverschieben ${e.model.columnResizeModeActivated() ? "deaktivieren" : "aktivieren"}`;
				});
				optionMenuItems.push({
					text: textActivatedDeactived,
					icon: "fa fa-arrows-h",
					width: 50,
					onClick: () => {
						try {
							e.model.columnResizeModeActivated(!e.model.columnResizeModeActivated());
							let isActivated = e.model.columnResizeModeActivated();
							let resizeMode = isActivated ? "widget" : "nextColumn";
							e.component.option("columnResizingMode", resizeMode);

							let currentState = e.component.state();
							currentState.columnResizingMode = resizeMode;
							e.component.state(currentState);

							MessageHelper.uiSuccess(`Alle Spalten mitverschieben wurde ${isActivated ? "aktiviert" : "deaktiviert"}`);
						} catch (err) {
							MessageHelper.uiError("Beim Setzen des Spalten Resizing Modus ist ein Fehler aufgetreten");
							Logger.error("Could not set/unset columnResizingMode:", err);
						}
					}
				});
			}

			if (options.layout.autoWidth) {
				if (e?.model?.columnAutoWidthActivated) {
					e.model.columnAutoWidthActivated(e.component.option("columnAutoWidth") === true);
					let textActivatedDeactived = ko.computed(() => {
						return `Autobreite ${e.model.columnAutoWidthActivated() ? "deaktivieren" : "aktivieren"}`;
					});
					optionMenuItems.push({
						text: textActivatedDeactived,
						icon: "fa fa-arrows-h",
						width: 50,
						onClick: () => {
							try {
								e.model.columnAutoWidthActivated(!e.model.columnAutoWidthActivated());
								let isActivated = e.model.columnAutoWidthActivated();
								e.component.option("columnAutoWidth", isActivated);

								let columnResizingMode = e.component.option("columnResizingMode");
								let currentState = e.component.state();

								if (isActivated && columnResizingMode === "widget") {
									// auto resizing doesn't work for "widget" resizing mode if a width property is set (24065)
									for (let item of currentState.columns) {
										delete item.width;
									}
								}
								currentState.columnAutoWidth = isActivated;
								e.component.state(currentState);

								MessageHelper.uiSuccess(`Die automatische Spaltenbreite wurde ${isActivated ? "aktiviert" : "deaktiviert"}`);
							} catch (err) {
								MessageHelper.uiError("Beim Setzen der automatischen Spaltenbreite ist ein Fehler aufgetreten");
								Logger.error("Could not set/unset columnAutoWidth:", err);
							}
						}
					});
				} else {
					Logger.warn("columnAutoWidthActivated missing in model für autoWidth menu item");
				}
			}
		}

		let containsExcelImport = false;

		if (excelExportItem != null) {
			containsExcelImport = true;
			toolbarItems.push(excelExportItem);
		}
		if (options.layout && options.layout.isEnabled) {
			toolbarItems.push({
				name: "changeLayout",
				widget: "dxMenu",
				options: {
					text: "",
					hint: "Layout bearbeiten",
					items: [
						{
							text: "",
							icon: "fa fa-cogs",
							items: optionMenuItems
						}]
				},
				location: "after"
			});
		}

		if (options.actions) {
			// add
			if (options.actions.add === true) {
				toolbarItems.push({
					template: "addRowTemplate",
					location: "after"
				});
			} else if (options.actions.add) {
				toolbarItems.push(options.actions.add);
			}
		}

		for (let item of toolbarItems) {
			item.locateInMenu = "never";
		}

		let result: IToolbarAppendResult = {
			toolbarItems,
			containsExcelImport,
		};

		return result;
	}

	/**
	 * Appends default toolbar items to a e.g. datagrid
	 * IMPORTANT: Needs data-columnchooser-enabled="true"
	 * to enable default columnchooser
	 * e.g. <div data-bind="dxDataGrid:{}" data-columnchooser-enabled="true">
	 */
	public static appendDefaultToolbarItems = (e: any, columnChooser: boolean | ColumnChooserType = ColumnChooserType.Standard) => {
		let options: IToolbarAppendOptions = { layout: { columnChooser } };
		let result = DefaultSettings.appendToolbarItems(e, options);
		return {
			toolbarItems: result.toolbarItems,
			addedExcelImport: result.containsExcelImport,
		};
	}

	/**
	* Orders selected items by reordering the DOM elements by their displayed text
	*/
	public static sortTagBoxOnValueChanged(e: any) {
		let sorted = e.element.find(".dx-tag").sort((a: Element, b: Element) => {
			let first = $(a).find(".dx-tag-content").text().trim().toLowerCase();
			let last = $(b).find(".dx-tag-content").text().trim().toLowerCase();
			if (first < last) { return -1; }
			if (first > last) { return 1; }
			return 0;
		});

		if (sorted.length > 0) {
			let parentNode = sorted[0].parentNode;
			// prepend in front of the dx.texteditor (search)
			sorted.prependTo(parentNode);
		}

		window.setTimeout(() => {
			DefaultSettings.sortTagBoxDataSourceOnOpened(e, true);
		}, 0);
	}

	/**
	* Orders elements in dropdown list so selected are on top
	*/
	public static sortTagBoxDataSourceOnOpened(e: any, onChange: boolean = false) {
		let tagBoxInstance: any = e.component;
		let dataSource: any[] | any = tagBoxInstance?.option("dataSource");
		let useItems = false;

		if (dataSource?.store) {
			// odata
			return;
		}

		if (!(dataSource?.length > 0)) {
			dataSource = tagBoxInstance?.option("items");
			useItems = true;
		}

		if (dataSource && dataSource?.length > 0 && Array.isArray(dataSource)) {
			let selection: number[] = null;
			// to prevent newly selected items from jumping to the top
			if (onChange && tagBoxInstance.hasOwnProperty("_onOpenedCurrentSelection")) {
				selection = tagBoxInstance["_onOpenedCurrentSelection"];
			} else {
				let sel = tagBoxInstance.option("value");
				selection = KoExtend.resolve(sel);
				tagBoxInstance["_onOpenedCurrentSelection"] = selection;
			}
			let displayExpr: string = tagBoxInstance.option("displayExpr") ?? "name";
			let valueExpr: string = tagBoxInstance.option("valueExpr") ?? "id";

			if (selection?.length > 0) {
				// TODO (oa) fix sorting for weekdays and other similar lookups
				dataSource.sort((a: any, b: any) => {
					if (a == null || b == null) {
						return 0;
					}
					if (selection.includes(a[valueExpr])) {
						if (selection.includes(b[valueExpr]) && a[displayExpr]?.localeCompare != null) {
							return a[displayExpr].localeCompare(b[displayExpr]);
						}
						return -1;
					}
					if (selection.includes(b[valueExpr])) {
						return 1;
					}
					if (a[displayExpr] == null || b[displayExpr] == null || a[displayExpr].localeCompare == null) {
						return 0;
					}

					return a[displayExpr].localeCompare(b[displayExpr]);
				});

				if (useItems) {
					e.component.option("items", dataSource);
				} else {
					e.component.option("dataSource", dataSource);
				}
			}
		}
	}

	/**
	* Orders selected items by reordering the DOM elements by id in an array.
	* Useful for e.g. weekdays/months etc.
	*/
	public static sortTagBoxByArrayPropOnValueChanged(data: any[] | KnockoutObservableArray<any>, searchProperty: string = "name") {
		let arr = KoExtend.resolve(data);
		return (e: any) => {
			let sorted = e.element.find(".dx-tag").sort((a: Element, b: Element) => {
				let first = $(a).find(".dx-tag-content").text().trim();
				let last = $(b).find(".dx-tag-content").text().trim();
				let firstId = arr.find((wkd) => wkd[searchProperty] === first)?.id ?? 0;
				let lastId = arr.find((wkd) => wkd[searchProperty] === last)?.id ?? 0;
				return firstId - lastId;
			});
			if (sorted.length > 0) {
				let parentNode = sorted[0].parentNode;
				// prepend in front of the dx.texteditor (search)
				sorted.prependTo(parentNode);
			}
		}
	}

	public static getInputSelectAllOption(): (e: any) => any {
		let validEditorNames: string[] = ["dxNumberBox", "dxTextBox", "dxDateBox"];
		return (e: any) => {
			if (!e || !e.component || (validEditorNames.indexOf(e.editorName) === -1)) {
				return;
			}
			try {
				let elem = e.editorElement[0].querySelector(".dx-texteditor-input");
				elem.selectionStart = 0;
				elem.selectionEnd = elem.value.length;
			} catch (err) {
				// ignore
			}
		};
	}

	public static readonly createActionColumn = (options?: dx.DataGridColumn): dx.DataGridColumn => {
		let column: dx.DataGridColumn = options || {};

		let actionColumn: dx.DataGridColumn = {
			caption: SharedLang.getDefaultSettingsColumnsAktion(),
			dataField: "ACTION_COLUMN",
			cellTemplate: "actionTemplate",
			dataType: "object",
			width: DefaultSettings.widthAction,
			alignment: DefaultSettings.actionAlignment,
			allowHiding: false,
			allowEditing: false,
			allowFiltering: false,
			allowHeaderFiltering: false,
			allowSorting: false,
			visible: true,
			fixed: true,
			cssClass: "actionColumn",
			fixedPosition: "right",
		};

		column = $.extend(true, {}, actionColumn, column);

		return column;
	}

	/**
	* Creates selection column
	* @param options Column options
	*/
	public static readonly createSelectionColumn = (options?: dx.DataGridColumn): dx.DataGridColumn => {
		let column: dx.DataGridColumn = options || {};

		let selectionColumn: dx.DataGridColumn = {
			caption: SharedLang.getDefaultSettingsColumnsAuswahl(),
			alignment: DefaultSettings.actionAlignment,
			width: DefaultSettings.widthBoolean,
			allowHiding: false,
			allowEditing: false,
			visible: true,
			cellTemplate: "chooseTemplate"
		};

		column = $.extend(true, {}, selectionColumn, column);

		return column;
	}

	/**
	* Creates internal id column for field "id" with default caption "Interne Id"
	* @param options Column options
	*/
	public static readonly createIdColumn = (options?: dx.DataGridColumn): dx.DataGridColumn => {
		let column: dx.DataGridColumn = options || {};

		let idColumn: dx.DataGridColumn = {
			caption: SharedLang.getDefaultSettingsColumnsInterneId(),
			width: DefaultSettings.widthNumber,
			alignment: DefaultSettings.numberAlignment,
			allowEditing: false,
			visible: false,
			dataField: "id",
			dataType: "number"
		};

		column = $.extend(true, {}, idColumn, column);

		return column;
	}

	/**
	 * Creates a summary
	 * @param options Summary options
	 */
	public static createSum(options: dx.DataGridTotalSummary & { column: string, valueFormat: DevExpress.ui.Format }) {
		let summary: dx.DataGridTotalSummary = {
			summaryType: "sum",
			alignment: "right",
		};

		return $.extend({}, summary, options);
	}

	public static decimalWithPrecisionFormat(scale: number = 2) {
		return `#,##0.${"0".repeat(scale)}`;
	}

	/**
	 * Create search buttons, DON'T FORGET TO USE ANONYMOUS FUNCTION!
	 * @param searchAction Search action
	 * @param cancelAction Cancel action
	 * @param cancelDisabled Cancel disabled
	 * @param onlyOptions Indicates to get only the options
	 */
	public static createSearchButtons(
		searchAction: () => void,
		cancelAction: () => void,
		cancelDisabled: KnockoutComputed<boolean>,
		onlyOptions?: boolean,
		okDisabled: KnockoutComputed<boolean> = ko.computed(() => false)): any[] {
		let cancelOptions = {
			hint: "Suche abbrechen",
			icon: "fa fa-times",
			onClick: () => cancelAction(),
			disabled: cancelDisabled
		};

		let searchOptions = {
			hint: "Suchen",
			icon: "fa fa-search",
			disabled: okDisabled,
			elementAttr: { class: "btn-primary" },
			onClick: () => {
				if (!cancelDisabled()) {
					cancelAction();
				}
				searchAction();
			},
		};

		if (onlyOptions) {
			return [cancelOptions, searchOptions];
		} else {
			return [
				{
					widget: "dxButton",
					location: "after",
					options: cancelOptions
				}, {
					widget: "dxButton",
					location: "after",
					options: searchOptions
				}
			];
		}
	}

	public static getLastColumnForComputed(): any {
		return {
			visible: true,
			name: "emptyColumn",
			width: "0.01%", // Bug 25177: Sonst gibt es im Chrome eine leere Spalte
			showInColumnChooser: false,
			allowReordering: false,
			allowSearch: false,
			allowSorting: false,
			allowGrouping: false,
			allowFixing: true,
			allowFiltering: false,
			allowEditing: false,
			allowHiding: true,
		};
	}

	public static closeItemMenu: (dxMenu: any) => void = (dxMenu) => {
		// close menu
		try {
			if (dxMenu && dxMenu.component && dxMenu.component.hide) {
				dxMenu.component.hide();
			}
		} catch (err) {
			Logger.error("Error while closing menu: ", err);
		}
	}

	private static getEditor(e: any): DevExpress.ui.dxHtmlEditor {
		if (!e || !e.element) {
			return null;
		}
		let $elem = $(e.element);
		if ($elem.length === 0) {
			return null;
		}

		let htmlEditor = $elem.closest(".dx-htmleditor");
		if (!htmlEditor || htmlEditor.length !== 1) {
			return null;
		}

		try {
			let editor = htmlEditor.dxHtmlEditor("instance");
			return editor;
		} catch (err) {
			Logger.warn(err);
		}
		return null;
	}

	private static getCalendarOptionsWithCalendarWeek(headerCaption: string = "KW"): DevExpress.ui.dxCalendarOptions {
		let lastDate: any = null;
		return {
			onContentReady: (e: any) => {
				if (e.component.option("zoomLevel") !== "month") {
					return;
				}
				window.setTimeout(() => {
					let header = $("<th>").addClass("calendarWeekCaption").css({ textAlign: "left" }).text(headerCaption);
					// TODO (oa) relative path?
					e.element.find(".dx-calendar-views-wrapper > div > table > thead > tr").prepend(header);
				}, 0);
			},
			onOptionChanged: (e) => {
				if (e.component.option("zoomLevel") !== "month") {
					return;
				}
				if (e.name === "currentDate" || (e.name === "zoomLevel" && e.value === "month")) {
					// check lastDate to check if first page change (otherwise 2nd caption from onContentReady will be shown)
					// e.value is not always a Date (just most of the time...)
					if (lastDate && new Date(lastDate).getMonth() !== new Date(e.value).getMonth()) {
						window.setTimeout(() => {
							// TODO (oa) relative path?
							let headerRow: any = e.element.find(".dx-calendar-views-wrapper > div > table > thead > tr");

							// otherwise there will be two "KW" every second page
							$(headerRow).remove(".calendarWeekCaption");

							let header = $("<th>").addClass("calendarWeekCaption").css({ textAlign: "left" }).text(headerCaption);
							headerRow.prepend(header);
						}, 0);
					}
					lastDate = e.value;
				}
			},
			cellTemplate: (itemData, itemIndex, itemElement): any => {
				if (itemData.view !== "month" || itemIndex !== 0) {
					return "cell";
				}
				let weekNumber = DateHelper.getCalendarWeek(itemData.date);
				let tr = itemElement.parent();
				let td = $("<td>");
				// TODO (oa) css class?
				$("<div>").text(weekNumber)
					.css("float", "leftleft")
					.css("margin-right", "25px")
					.appendTo(td);
				td.on("click", (e: any) => {
					e.stopPropagation();
					e.preventDefault();
				}).css("cursor", "default").prependTo(tr);
				$("<div>").text(itemData.text).appendTo(itemElement);
			},
		};
	}
}

export interface IToolbarAppendOptions {
	layout?: {
		columnChooser?: boolean | ColumnChooserType;
		save?: boolean;
		reset?: boolean;
		copy?: boolean;
		autoWidth?: boolean;
		resizingMode?: boolean;
		isEnabled?: boolean;
	};
	reload?: boolean;
	filter?: boolean;
	excelExport?: boolean;
	actions?: {
		add?: boolean | DevExpress.ui.dxToolbarItem;
	};
}

export interface IToolbarAppendResult {
	toolbarItems: any[];
	containsExcelImport: boolean;
}

/**
 * Freeze default settings.
 */
function freeze<T>(opt: T): T {
	// TODO(jpg: always freeze default settings in the future.
	if (Debugging.getInstance().isEnabled() && Object.freeze) {
		opt = Object.freeze(opt);
	}
	return opt;
}